import { useMutation } from "@tanstack/react-query";

import { useCurrentCompanyId } from "@/hooks/useCurrentCompanyId";
import { useCurrentJobId } from "@/hooks/useCurrentJobId";
import { useSetCandidacyInfo } from "@/hooks/sets/useSetCandidacyInfo";
import { guacHttpClient } from "@/lib/http";
import { novaClient } from "@/lib/trpc";
import { GenerateCandidacyArtifactsResponse, HttpResponse } from "@/types";

// Regenerates the Laborup resume (set as primary) and, when the candidacy has
// an exec summary, the stored exec summary + resume packet. Active users only.
export function useCreateAddCandidacyArtifacts() {
  const currentCompanyId = useCurrentCompanyId();
  const currentJobId = useCurrentJobId();
  const novaClientUtils = novaClient.useUtils();
  const setCandidacyInfo = useSetCandidacyInfo();

  return useMutation({
    mutationFn: async ({
      candidacyId,
    }: {
      candidacyId: string;
      talentId: number | null;
    }) => {
      const path = `companies/${currentCompanyId}/jobs/${currentJobId}/candidacies/${candidacyId}/artifacts/generate`;
      const res = await guacHttpClient.post<
        GenerateCandidacyArtifactsResponse,
        HttpResponse<GenerateCandidacyArtifactsResponse>
      >(path);
      return res.data;
    },
    onSuccess: ({ packetUrl }, { candidacyId, talentId }) => {
      if (talentId !== null) {
        void novaClientUtils.internal.user.resume.active.listByUserId.invalidate(
          { userId: talentId },
        );
      }
      // The v2 sheet's records tab lists resumes (and the primary resume id)
      // from this query. Invalidated without input so a jobId that doesn't
      // match the sheet's can't quietly leave the new resume out of the list.
      void novaClientUtils.internal.user.active.getCandidacySheetData.invalidate();
      if (packetUrl) {
        setCandidacyInfo({ candidacyId, execSummaryPacketUrl: packetUrl });
      }
    },
  });
}
